import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { saveErrorText } from '../utils/apiError';

interface StatusChangeModalProps {
  type: 'on_hold' | 'resume';
  onSubmit: (message: string) => Promise<void>;
  onClose: () => void;
}

export default function StatusChangeModal({ type, onSubmit, onClose }: StatusChangeModalProps) {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const config = {
    on_hold: { key: 'onHold', icon: 'pause-circle', color: 'warning' },
    resume: { key: 'resume', icon: 'play-circle', color: 'success' }
  }[type];
  const text = (name: string) => t(`statusChangeModal.${config.key}.${name}`);

  const handleSubmit = async () => {
    if (isSubmitting || (type !== 'resume' && !message.trim())) return;
    setIsSubmitting(true);
    setError('');
    try {
      await onSubmit(message.trim() || t('statusChangeModal.resume.defaultMessage'));
    } catch (submitError) {
      // The message stays, so the user can try again (ClickUp 869fgxq45).
      setError(saveErrorText(t, submitError, 'statusChangeModal.saveError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      onClose={() => { if (!isSubmitting) onClose(); }}
      title={(
        <>
          <i className={`feather-${config.icon} me-2 text-${config.color}`} aria-hidden="true"></i>
          {text('title')}
        </>
      )}
      footer={(
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className={`btn btn-${config.color}`}
            onClick={() => void handleSubmit()}
            disabled={isSubmitting || (type !== 'resume' && !message.trim())}
          >
            <i className={`feather-${config.icon} me-1`} aria-hidden="true"></i>
            {isSubmitting ? t('common.saving') : text('button')}
          </button>
        </>
      )}
    >
      <p className="text-muted small mb-3">{text('description')}</p>
      <div className="mb-3">
        <label className="form-label small fw-semibold" htmlFor="status-change-message">
          {text('label')}
        </label>
        <textarea
          id="status-change-message"
          className="form-control"
          rows={4}
          placeholder={text('placeholder')}
          value={message}
          onChange={e => setMessage(e.target.value)}
          disabled={isSubmitting}
          autoFocus
        />
      </div>
      {error && (
        <div className="alert alert-danger mb-0" role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}
